/**
 * Who wears the speaking ring right now.
 *
 * Not the same question as "who is the active speaker". That one is sticky on
 * purpose — a silent room keeps its last speaker, so the stage and the seating
 * on page one do not drop back between sentences. A ring that followed it
 * stayed lit on someone who had stopped talking minutes ago.
 *
 * So the ring is the active speaker WHILE they are audible: in the SFU's latest
 * list of who is above the noise floor. The SFU sends that list every 500 ms it
 * changes, and an empty one on silence, so a pause between words reads as
 * silence for half a second. Turning the ring off on the first silent tick made
 * it blink through every sentence; it is released only after `holdMs` of
 * silence, and a word inside that window keeps it lit.
 *
 * Someone who is no longer the active speaker loses the ring at once — holding
 * it would show two people speaking, or the wrong one.
 */

/** How long the ring outlasts the speaker's last audible tick. */
export const SPEAKING_RING_HOLD_MS = 1500

export type SpeakingRing = {
  /** Feed the current active speaker and the SFU's latest audible list. */
  update: (activeSpeakerId: string | null, audible: readonly string[]) => void
  /** Cancel a pending release; call on unmount. */
  dispose: () => void
}

export function createSpeakingRing(
  onChange: (speakingId: string | null) => void,
  holdMs: number = SPEAKING_RING_HOLD_MS,
): SpeakingRing {
  let current: string | null = null
  let releaseTimer: ReturnType<typeof setTimeout> | null = null

  const cancelRelease = () => {
    if (releaseTimer) {
      clearTimeout(releaseTimer)
      releaseTimer = null
    }
  }
  const set = (id: string | null) => {
    cancelRelease()
    if (id === current) return
    current = id
    onChange(id)
  }

  return {
    update(activeSpeakerId, audible) {
      if (activeSpeakerId !== null && audible.includes(activeSpeakerId)) {
        set(activeSpeakerId)
        return
      }
      // Ring on someone who is no longer the active speaker: off now.
      if (current !== activeSpeakerId) {
        set(null)
        return
      }
      // The speaker went quiet. Release after the hold unless they are heard
      // again first — an already-running release is not restarted, so steady
      // silence ends the ring `holdMs` after it began.
      if (current !== null && !releaseTimer) {
        releaseTimer = setTimeout(() => {
          releaseTimer = null
          current = null
          onChange(null)
        }, holdMs)
      }
    },
    dispose: cancelRelease,
  }
}
